/// <amd-module name="Build/Designer/Wizard" />
import * as React from "react";

import { IBuildLinkingService } from "Build/Client/Linking";
import { openLinkSecurely } from "Build/Common/Library/Navigation";
import { TemplateAssetTypes } from "Build/Designer/Constants";
import { PageLoadScenarios, PerformanceConstants } from "Build/Designer/Performance";
import * as Resources from "Build/Designer/Resources";
import { TemplateSelector } from "Build/Designer/TemplateSelector";
import { ConfigurationFileInfo, FileAsset, ISourceProviderTile, RepositoryAnalysisInfo,
         TemplateInfo, WizardState } from "Build/Designer/Wizard.types";
import { IWizardListItem, WizardList } from "Build/Designer/WizardList";
import { YamlWizardPage } from "Build/Designer/YamlWizardPage";

import { IReadonlyObservableValue, ObservableValue } from "VSS/Core/Observable";
import * as StringUtil from "VSS/Core/Util/String";
import { FPSLink } from "VSS/Features/PlatformUI/FPSLink";
import { FormatComponent } from "VSS/Platform/Components/Format";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { onClickFPS } from "VSS/Platform/FPS";
import { INavigationStateChangedEvent, IVssHistoryService, StateMergeOptions } from "VSS/Platform/History";
import { Components, css, IVssComponentContext, IVssComponentDetail, IVssLayoutManager, VssComponent, WrappedComponent } from "VSS/Platform/Layout";
import { getAssetLocation } from "VSS/Platform/Location";
import { IVssPerformanceService } from "VSS/Platform/Performance";
import { IVssTelemetryService } from "VSS/Platform/Telemetry";

import { Orientation, Page } from "VSSUI/Page";
import { Surface, SurfaceBackground } from "VSSUI/Surface";
import { IWizardItem, Wizard, WizardItemState } from "VSSUI/Wizard";

import { GeneralSettingsData } from 'Build/Client/Contracts/DataProvider';

import { Observer } from "VSSUI/Observer";

import { IGeneralSettingsService } from "Build/Settings/general/GeneralSettingsService";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";

export interface Props {
}

export interface State {
    stepNumber: number;
    sourceProviderKey?: string;
    configuration?: ConfigurationFileInfo;
    repositorySubheader?: string;
    repositoryUrl?: string;
    templateSubheader?: string;
    template?: TemplateInfo;
    repoAnalysis?: RepositoryAnalysisInfo;
    showSaveAndRun: boolean;
    wizardState: WizardState;
    isClassicBuildPipelineCreationDisabled: boolean;
    isClassicReleasePipelineCreationDisabled: boolean;
}

export class DesignerWizardContent extends VssComponent<Props, State, IVssComponentContext> {
    private readonly _sourceProviders: ISourceProviderTile[];
    private readonly _configurationPath: string;
    private _ttiMarked: boolean = false;
    private _projectSettingsService: IGeneralSettingsService;

    constructor(props: Props, context: IVssComponentContext) {
        super(props, context);

        this._configurationPath = "/azure-pipelines.yml";

        const historyService = this.context.pageContext.getService<IVssHistoryService>("IVssHistoryService");
        const state = historyService.getState();

        this._projectSettingsService = this.context.pageContext.getService<IGeneralSettingsService>("IGeneralSettingsService");

        // Load source providers before looking at state since it could have the source provider key
        const contributionService = this.context.pageContext.getService<IVssContributionService>("IVssContributionService");
        const contributionMap = contributionService.getContributionsByType<ISourceProviderTile>("ms.vss-build-web.acquisition-source-provider-tile");
        this._sourceProviders = Object.keys(contributionMap).map(key => contributionMap[key]).sort((a, b) => a.name.localeCompare(b.name, navigator.language));

        if (state) {
            this.state = this._readStateFromHistory(state);
        }
        else {
            this.state = {
                stepNumber: 1,
                showSaveAndRun: true,
                wizardState: {
                },
                isClassicBuildPipelineCreationDisabled: this._getBuildPipelineCreationValue(),
                isClassicReleasePipelineCreationDisabled: this._getReleasePipelineCreationValue()
            };
        }
        this._recordTelemetryInfo();
    }

    public componentDidMount() {
        const historyService = this.context.pageContext.getService<IVssHistoryService>("IVssHistoryService");
        historyService.subscribe(this._onHistoryChanged);
        if (this._projectSettingsService.data){
            this._projectSettingsService.data.subscribe(this._onProjectSettingsChanged);
        }

        if (this.state.stepNumber == 1) {
            this._markTTI(PageLoadScenarios.Sources);
        }
    }

    public componentWillUnmount() {
        const historyService = this.context.pageContext.getService<IVssHistoryService>("IVssHistoryService");
        historyService.unsubscribe(this._onHistoryChanged);
        if (this._projectSettingsService.data){
            this._projectSettingsService.data.unsubscribe(this._onProjectSettingsChanged);
        }
    }

    public render(): JSX.Element {
        const isEditorView = this.state.stepNumber === 4;
        return (
            <Page className={css("ci-designer-wizard-view flex-column flex-grow", isEditorView ? "bolt-page-grey" : "")}>
                <Wizard
                    className={"wizard-view-step-list page-content page-content-top flex-noshrink"}
                    items={this._getWizardItems(this.state.stepNumber)}
                    selectedIndex={this.state.stepNumber - 1}
                />
                <div className={css("wizard-view-detail flex-column flex-grow", isEditorView ? "" : "page-content")}>
                    {this._renderDetail()}
                </div>
            </Page>
        );
    }

    private _renderDetail(): JSX.Element {
        switch (this.state.stepNumber) {
            default:
            case 1:
                return this._renderSourceProviderSelector();
            case 2:
                return this._getSourceProviderWizard();
            case 3:
                return <TemplateSelector wizardState={this.state.wizardState} onTemplateSelected={this._onTemplateSelected} />
            case 4:
                return this._renderYamlWizardPage();
        }
    }

    private _renderSourceProviderSelector(): JSX.Element {
        const disableBuildPipelineCreation = this.state.isClassicBuildPipelineCreationDisabled; 
        var sourceProviders = [...this._sourceProviders]
        if (disableBuildPipelineCreation) {
            sourceProviders = sourceProviders.filter(sp => (sp.key != 'git' && sp.key != 'svn'));
        }
        return <div className="source-provider-selector">
            <div className="header-section">
                <div className="subheader  body-m secondary-text">{Resources.NewPipeline}</div>
                <div className="header title-l">{Resources.WhereIsYourCode}</div>
            </div>
            <WizardList
                wizardListItems={this._mapToWizardListItems(sourceProviders)}
                onClick={(event, data) => {
                    this._onSourceProviderClick(sourceProviders[data.index]);
                }}
            />
            {!disableBuildPipelineCreation &&
                        <div className="old-designer-fallback">
                            <FormatComponent format={Resources.OldDesignerFallbackFormat}>
                                <FPSLink
                                    href={this._getFallbackLink()}
                                    telemetrySource="new-pipeline-wizard.old-designer"
                                    telemetryProperties={{ telemetrySession: this.state.wizardState.telemetrySession }}>
                                    {Resources.OldDesignerLinkText}
                                </FPSLink>
                            </FormatComponent>
                        </div>
            }
        </div>;
    }

    private _renderYamlWizardPage(): JSX.Element {
        const path = this.state.configuration ? this.state.configuration.path : this.state.wizardState.configPath ?
            this.state.wizardState.configPath : this._configurationPath;
        return <YamlWizardPage
            wizardState={this.state.wizardState}
            content={this.state.template ? this.state.template.content : this.state.configuration!.content}
            path={path}
            url={this.state.configuration ? this.state.configuration.webUrl : undefined}
            onAction={this._recordTelemetryInfo}
            onHistoryChanged={this._onHistoryChanged}
            contentSha={this.state.configuration ? this.state.configuration.objectId : ""}
            redirectToPipeline={this._redirectToPipeline}
            additionalFiles={this._getAdditionalFilesForTemplate(this.state.template)}
        />
    }

    private _mapToWizardListItems = (sourceProviders: ISourceProviderTile[]): IWizardListItem[] => {
        return sourceProviders.map(s => {
            const labels = [];
            if (s.supportsYaml) {
                labels.push(Resources.Yaml);
            }
            return {
                name: s.name,
                description: s.description,
                iconUrl: s.iconAsset ? getAssetLocation(this.context.pageContext, s.iconAsset) : undefined,
                iconAltText: s.iconAssetAltText,
                iconName: s.iconName,
                labels: labels
            } as IWizardListItem;
        });
    }

    private _onHistoryChanged = (value: INavigationStateChangedEvent): void => {
        this.setState(this._readStateFromHistory(value.newState!.state));
    }

    private _getBuildPipelineCreationValue = (): boolean => {
        if(isFeatureFlagEnabled(this.context.pageContext, "Build2.NewTogglesToDisableClassicPipelineCreation", false)) {
            return this._projectSettingsService.data?.value.disableClassicBuildPipelineCreation ?
                   this._projectSettingsService.data?.value.disableClassicBuildPipelineCreation.enabled : true;
        }
        else {
            return this._projectSettingsService.data?.value.disableClassicPipelineCreation ?
                   this._projectSettingsService.data?.value.disableClassicPipelineCreation.enabled : true;
        }
    }

    private _getReleasePipelineCreationValue = (): boolean => {
        if(isFeatureFlagEnabled(this.context.pageContext, "Build2.NewTogglesToDisableClassicPipelineCreation", false)) {
            return this._projectSettingsService.data?.value.disableClassicReleasePipelineCreation ?
                   this._projectSettingsService.data?.value.disableClassicReleasePipelineCreation.enabled : true;
        }
        else {
            return this._projectSettingsService.data?.value.disableClassicPipelineCreation ?
                   this._projectSettingsService.data?.value.disableClassicPipelineCreation.enabled : true;
        }
    }

    private _onProjectSettingsChanged = (projectSettings: GeneralSettingsData): void => {
        this.setState({
            isClassicBuildPipelineCreationDisabled: this._getBuildPipelineCreationValue(),
            isClassicReleasePipelineCreationDisabled: this._getReleasePipelineCreationValue()
        })
    }

    private _readStateFromHistory(state: { [key: string]: string }): State {
        let stepNumber = 1;
        let sourceProvider: ISourceProviderTile | undefined = undefined;
        let repositorySubheader: string | undefined = undefined;
        let repositoryUrl: string | undefined = undefined;

        const sourceProviderKey = state["sourceProvider"];
        if (sourceProviderKey) {
            sourceProvider = this._getSourceProviderTile(sourceProviderKey);
            if (sourceProvider) {
                stepNumber = 2;
            }
        }

        const sourceProviderComplete = state["sourceProviderComplete"] === "true";
        if (sourceProviderComplete) {
            stepNumber = 3;
            repositorySubheader = state["repositoryName"];
            repositoryUrl = state["repositoryUrl"];
        }

        return {
            stepNumber: stepNumber,
            sourceProviderKey: sourceProviderKey,
            repositorySubheader: repositorySubheader,
            repositoryUrl: repositoryUrl,
            showSaveAndRun: true,
            templateSubheader: "",
            wizardState: { ...state, branch: this._normalizeBranchName(state.branch) },
            isClassicBuildPipelineCreationDisabled: this._getBuildPipelineCreationValue(),
            isClassicReleasePipelineCreationDisabled: this._getReleasePipelineCreationValue()
        };
    }

    private _onSourceProviderClick = (sourceProvider: ISourceProviderTile): void => {
        if (sourceProvider.isClassic) {
            const classicEditorLink = this._getClassicEditorLink(sourceProvider.key);
            const telemetrySource = "new-pipeline-wizard.old-designer";
            const telemetryProperties = { telemetrySession: this.state.wizardState.telemetrySession, sourceProvider: sourceProvider.key };
            onClickFPS(this.context.pageContext, classicEditorLink, true, undefined, { telemetrySource, telemetryProperties });
        }
        else {
            const historyService = this.context.pageContext.getService<IVssHistoryService>("IVssHistoryService");
            // make sure that 'telemetrySession' is set before calls to 'generateUrl'
            const url = historyService.generateUrl({
                "sourceProvider": sourceProvider.key,
                "telemetrySession": (this.state.wizardState && this.state.wizardState.telemetrySession) || this._createTelemetrySession()
            }, StateMergeOptions.currentState);
            historyService.pushState(undefined, url);

            // pushing state doesn't invoke the listener
            this.setState({
                sourceProviderKey: sourceProvider.key,
                repositorySubheader: "",
                templateSubheader: "",
                stepNumber: 2,
                wizardState: historyService.getState()
            }, () => this._recordTelemetryInfo());
        }
    }

    private _onSourceProviderComplete = (updatedState?: { [key: string]: string }, properties?: { [key: string]: string }): void => {
        if (!updatedState) {
            updatedState = {};
        }
        updatedState["sourceProviderComplete"] = "true";
        updatedState["telemetrySession"] = (this.state.wizardState && this.state.wizardState.telemetrySession) || this._createTelemetrySession();

        const historyService = this.context.pageContext.getService<IVssHistoryService>("IVssHistoryService");

        // make sure that 'telemetrySession' is set before calls to 'generateUrl'
        const url = historyService.generateUrl(updatedState, StateMergeOptions.currentState);
        historyService.pushState(undefined, url);

        // pushing state doesn't invoke the listener
        if (!properties) {
            properties = {};
        }
        this.setState({
            stepNumber: 3,
            repositorySubheader: updatedState["repositoryName"] || "",
            repositoryUrl: updatedState["repositoryUrl"],
            templateSubheader: "",
            wizardState: { ...this.state.wizardState, ...updatedState, ...properties }
        }, () => this._recordTelemetryInfo());
    }

    /**
     * A YAML 'template' has been selected or an existing YAML 'configuration' file found.
     * If template !== undefined: The user selected a template to provide the content for the new yaml/config file.
     * If configuration & branch !== undefined: The user selected an existing file from a specific branch to use.
     * configPath: The default config/yaml file name for templates (example: /azure-pipelines.yml).
     */
    private _onTemplateSelected = (template?: TemplateInfo, repoAnalysis?: RepositoryAnalysisInfo, configuration?: ConfigurationFileInfo, branch?: string, configPath?: string): void => {
        // a template or yaml file was selected
        if (template || branch) {
            const updatedState = {
                "branch": branch || (this.state.wizardState && this.state.wizardState.branch) || "",
                "template": template ? template.name : (this.state.template ? this.state.template.name : ""),
                "telemetrySession": (this.state.wizardState && this.state.wizardState.telemetrySession) || this._createTelemetrySession()
            };
            const historyService = this.context.pageContext.getService<IVssHistoryService>("IVssHistoryService");

            // make sure that 'telemetrySession' is set before calls to 'generateUrl'
            const url = historyService.generateUrl(updatedState, StateMergeOptions.currentState);
            historyService.pushState(undefined, url);
        }

        this._onTemplateSelectionComplete(4, template, repoAnalysis, configuration, branch, configPath);
    }

    private _onTemplateSelectionComplete(nextStep: number, template?: TemplateInfo, repoAnalysis?: RepositoryAnalysisInfo, configuration?: ConfigurationFileInfo, branch?: string, configPath?: string): void {
        this.setState({
            stepNumber: nextStep,
            template: template,
            repoAnalysis: repoAnalysis,
            configuration: configuration,
            templateSubheader: template ? template.name : Resources.ExistingFileFound,
            showSaveAndRun: !!template,
            wizardState: { ...this.state.wizardState, branch: branch || this.state.wizardState.branch, configPath: configPath || "" }
        }, () => this._recordTelemetryInfo());
    }

    private _getSourceProviderTile = (key: string): ISourceProviderTile | undefined => {
        return this._sourceProviders.find(sp => StringUtil.equals(sp.key, key, true));
    }

    private _getSourceProviderWizard(): JSX.Element {
        const layoutManager = this.context.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
        const wizards = layoutManager.getComponentsForRegion("sourceProviderWizard");
        const wizard = wizards.value.find(w => {
            const key = w.componentProperties!["key"];
            if (key) {
                return StringUtil.equals(key.toString(), this.state.sourceProviderKey!, true);
            }
            return false;
        });
        if (wizard) {
            return <WrappedComponent wrappedType={wizard.componentType} {...wizard.componentProperties}
                wizardState={this.state.wizardState}
                onComplete={this._onSourceProviderComplete}
                onUpdate={this._recordTelemetryInfo}
                onTTI={this._markRepositoryTTI} />
        }
        else {
            return <div>The source provider key '{this.state.sourceProviderKey!}' was not recognized.</div>;
        }
    }

    private _recordTelemetryInfo = (action?: string, additionalTelemetry?: { [key: string]: any }): void => {
        const setTelemetryValueOrDefault = (telemetry: { [key: string]: any }, key: string, defaultValue?: string) => {
            if (this.state.wizardState && this.state.wizardState[key]) {
                telemetry[key] = this.state.wizardState[key];
            }
            else if (defaultValue) {
                telemetry[key] = defaultValue;
            }
        };

        const setTelemetryBoolValueOrDefault = (telemetry: { [key: string]: any }, key: string) => {
            if (this.state.wizardState && this.state.wizardState[key]) {
                telemetry[key] = (this.state.wizardState[key].toLowerCase() === "true");
            }
        };

        const telemetryData: { [key: string]: any } = {};
        if (action) {
            telemetryData.action = action;
        }
        else {
            switch (this.state.stepNumber) {
                case 1: telemetryData.action = "ChooseConnection"; break;
                case 2: telemetryData.action = "ChooseRepo"; break;
                case 3: telemetryData.action = "ChooseTemplate"; break;
                case 4: telemetryData.action = "EditYaml"; break;
                default: telemetryData.action = "unknown";
            }
        }

        setTelemetryValueOrDefault(telemetryData, "externalOwnerId");
        setTelemetryValueOrDefault(telemetryData, "externalOwnerName");
        setTelemetryBoolValueOrDefault(telemetryData, "externalOwnerIsAUser");

        setTelemetryValueOrDefault(telemetryData, "entryPoint");
        setTelemetryBoolValueOrDefault(telemetryData, "repositoryIsFork");
        setTelemetryBoolValueOrDefault(telemetryData, "repositoryIsPrivate");
        setTelemetryValueOrDefault(telemetryData, "telemetrySession", this._createTelemetrySession());

        // use 'safe repository' which doesn't include sensitive data (such as passwords in the URL).
        if (this.state.wizardState && this.state.wizardState.safeRepository) {
            telemetryData.repositoryName = this.state.wizardState.safeRepository;
        }

        const sourceProvider = this._getSourceProviderTile(this.state.sourceProviderKey || "");
        telemetryData.repositoryType = (sourceProvider && sourceProvider.name) || "";
        if (this.state.template && this.state.template.name) {
            telemetryData.templateName = this.state.template.name;
        }

        if (this.state.repoAnalysis && this.state.repoAnalysis.recommendTemplate) {
            telemetryData.recommendedTemplateName = this.state.repoAnalysis.recommendTemplate.name;
            telemetryData.suggestedTemplateNames = this.state.repoAnalysis.suggestedTemplates.map(x => x.name);
        }

        // additional telemetry that isn't related to state
        if (additionalTelemetry) {
            for (const key in additionalTelemetry) {
                telemetryData[key] = additionalTelemetry[key];
            }
        }

        const telemetryService = this.context.pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
        telemetryService.publishEvent(
            "Build",
            "CreatePipeline",
            telemetryData);
    }

    private _createTelemetrySession(): string {
        // Unique~ish ID that begins when a user selects the repo type
        return StringUtil.newGuid();
    }

    private _markRepositoryTTI = () => {
        this._markTTI(PageLoadScenarios.Repository);
    }

    private _markTTI = (scenario: string) => {
        if (!this._ttiMarked) {
            this._ttiMarked = true;
            this.context.pageContext.getService<IVssPerformanceService>("IVssPerformanceService").markTimeToInteractive(PerformanceConstants.Area, scenario);
        }
    }

    private _getWizardItems = (stepNumber: number): IWizardItem[] => {
        return [Resources.Connect, Resources.Select, Resources.Configure, Resources.Review]
            .map((title, index) => {
                return {
                    title: title,
                    itemState: new ObservableValue<WizardItemState>(stepNumber > (index + 1) ? WizardItemState.Complete : WizardItemState.None),
                }
            });
    }

    private _getFallbackLink = (): string => {
        const linkingService = this.context.pageContext.getService<IBuildLinkingService>("IBuildLinkingService");
        // append &id=0 to inform the route handler not to go to the new route
        return linkingService.getNewDefinitionEditUrl(this.state.wizardState.pipelinePath) + "&id=0";
    }

    private _getClassicEditorLink = (key: string): string => {
        // append &repositoryType to preselect the classic provider in the editor
        return this._getFallbackLink() + "&repositoryType=" + key;
    }

    private _redirectToPipeline = (pipelineUrl: string) => {
        if (this.state.stepNumber == 4) {
            openLinkSecurely(pipelineUrl);
        }
    }

    private _normalizeBranchName = (branch: string): string => {
        const refsHeadsPrefix = "refs/heads/";
        if (branch && StringUtil.startsWith(branch, refsHeadsPrefix, true)) {
            return branch.substring(refsHeadsPrefix.length);
        }
        return branch;
    }

    private _getAdditionalFilesForTemplate = (template?: TemplateInfo): FileAsset[] | undefined => {
        if (template && template.assets) {
            return template.assets.filter((asset) => {
                return asset && StringUtil.equals(asset.type, TemplateAssetTypes.File, true);
            });
        }
    }
}

Components.add("ci-designer-wizard-view", DesignerWizardContent);
